import { Result, ok, err } from "neverthrow";
import { ParseError } from "../../shared/errors/parseError";
import {
  HttpLostFoundDetail,
  HttpLostFoundDetailResponse,
  ILostFoundDetailParser,
} from "../bbsRepository.types";
import { injectable } from "inversify";
import { LostFoundTypeCode } from "@/types";
import dayjs from "dayjs";
import { ILostFoundDetail } from "@/domain/entities/bbs/lostFound/lostFoundDetail.types";

function parseLostFoundDetail(data: HttpLostFoundDetail): ILostFoundDetail {
  const lostFound: ILostFoundDetail = {
    type: data.lostTs ? LostFoundTypeCode.Lost : LostFoundTypeCode.Found,
    uid: data.uid,
    allocUid: data.allocUid,
    title: data.title,
    foundTs: data.foundTs
      ? dayjs(data.foundTs).format("YYYY-MM-DD HH:mm")
      : undefined,
    lostTs: data.lostTs
      ? dayjs(data.lostTs).format("YYYY-MM-DD HH:mm")
      : undefined,
    statusTs: dayjs(data.statusTs).format("YYYY-MM-DD HH:mm:ss"),
    createdTs: dayjs(data.createdTs).format("YYYY-MM-DD HH:mm:ss"),
    createdUid: data.createdUid,
    updatedTs: data.updatedTs
      ? dayjs(data.updatedTs).format("YYYY-MM-DD HH:mm:ss")
      : undefined,
    savedTs: dayjs(data.savedTs).format("YYYY-MM-DD HH:mm:ss"),
    lineTurnUid: data.lineTurnUid,
    lineTurnNo: data.lineTurnNo,
    lineTurnName: data.lineTurnName,
    lineTurnDisplayText: data.lineTurnDisplayText,
    serviceUid: data.serviceUid,
    serviceTypeCode: data.serviceTypeCode,
    serviceTypeName: data.serviceTypeName,
    serviceName: data.serviceName,
    lineGroupUid: data.lineGroupUid,
    lineGroupNo: data.lineGroupNo,
    lineGroupName: data.lineGroupName,
    lineGroupDisplayText: data.lineGroupDisplayText,
    lineRouteUid: data.lineRouteUid,
    lineRouteNo: data.lineRouteNo,
    lineRouteDisplayText: data.lineRouteDisplayText,
    lineRouteName: data.lineRouteName,
    lineName: data.lineName,
    campusName: data.campusName,
    lineTypeCode: data.lineTypeCode,
    lineTypeName: data.lineTypeName,
    savedPhone: data.savedPhone,
    updatedReason: data.updatedReason,
    storeLocationName: data.storeLocationName,
    storeBeginDtm: data.storeBeginDtm,
    storeLocationCode: data.storeLocationCode,
    note: data.note,
    lostNote: data.lostNote,
    statusCode: data.statusCode,
    statusName: data.statusName,
    updatedUid: data.updatedUid,
    updatedName: data.updatedName,
    createdName: data.createdName,
    savedUid: data.savedUid,
    savedName: data.savedName,
  };
  return lostFound;
}

function toHttpLostFoundDetail(data: ILostFoundDetail): HttpLostFoundDetail {
  const lostFound: HttpLostFoundDetail = {
    uid: data.uid,
    allocUid: data.allocUid ? data.allocUid : undefined,
    title: data.title,
    foundTs: data.foundTs ? dayjs(data.foundTs).format() : undefined,
    lostTs: data.lostTs ? dayjs(data.lostTs).format() : undefined,
    statusTs: dayjs(data.statusTs).format(),
    createdTs: dayjs(data.createdTs).format(),
    createdUid: data.createdUid,
    updatedTs: data.updatedTs ? dayjs(data.updatedTs).format() : undefined,
    savedTs: dayjs(data.savedTs).format(),
    lineTurnUid: data.lineTurnUid,
    lineTurnNo: data.lineTurnNo,
    lineTurnName: data.lineTurnName,
    lineTurnDisplayText: data.lineTurnDisplayText,
    serviceUid: data.serviceUid,
    serviceTypeCode: data.serviceTypeCode,
    serviceTypeName: data.serviceTypeName,
    serviceName: data.serviceName,
    lineGroupUid: data.lineGroupUid,
    lineGroupNo: data.lineGroupNo,
    lineGroupName: data.lineGroupName,
    lineGroupDisplayText: data.lineGroupDisplayText,
    lineRouteUid: data.lineRouteUid,
    lineRouteNo: data.lineRouteNo,
    lineRouteDisplayText: data.lineRouteDisplayText,
    lineRouteName: data.lineRouteName,
    lineName: data.lineName ? data.lineName : undefined,
    campusName: data.campusName,
    lineTypeCode: data.lineTypeCode,
    lineTypeName: data.lineTypeName,
    savedPhone: data.savedPhone,
    updatedReason: data.updatedReason,
    storeLocationName: data.storeLocationName,
    storeBeginDtm: data.storeBeginDtm,
    storeLocationCode: data.storeLocationCode,
    note: data.note,
    lostNote: data.lostNote,
    statusCode: data.statusCode,
    statusName: data.statusName,
    updatedUid: data.updatedUid,
    updatedName: data.updatedName,
    createdName: data.createdName,
    savedUid: data.savedUid,
    savedName: data.savedName,
  };
  return lostFound;
}

@injectable()
export class LostFoundDetailParser implements ILostFoundDetailParser {
  toDomain(
    response: HttpLostFoundDetailResponse
  ): Result<ILostFoundDetail, ParseError> {
    try {
      const lostFoundDetail = parseLostFoundDetail(response.data);
      return ok(lostFoundDetail);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }

  fromDomain(data: ILostFoundDetail): HttpLostFoundDetail {
    try {
      return toHttpLostFoundDetail(data);
    } catch (error: any) {
      throw new Error(error.message);
    }
  }
}
